import React, { useState } from 'react'
import useStyles from '../styles'

interface JsonViewProps {
  data: unknown
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

const PrimitiveValue: React.FC<{ value: unknown }> = ({ value }) => {
  const classes = useStyles()

  if (value === null || value === undefined) {
    return <span className={classes.jsonNull}>null</span>
  }
  if (typeof value === 'boolean') {
    return <span className={classes.jsonBool}>{String(value)}</span>
  }
  if (typeof value === 'number') {
    return <span className={classes.jsonNumber}>{value}</span>
  }
  return <span className={classes.jsonString}>{String(value)}</span>
}

interface JsonNodeProps {
  name: string
  value: unknown
  depth: number
}

const JsonNode: React.FC<JsonNodeProps> = ({ name, value, depth }) => {
  const classes = useStyles()

  const [open, setOpen] = useState(depth < 2)

  const isObject = isPlainObject(value)
  const isArray = Array.isArray(value)
  const isBranch = isObject || isArray

  if (!isBranch) {
    return (
      <div
        className={classes.jsonRow}
        style={{ paddingLeft: depth * 14 }}
      >
        <span className={classes.jsonKey}>{name}</span>
        <span className={classes.jsonColon}>:</span>
        <PrimitiveValue value={value} />
      </div>
    )
  }

  const entries = isArray
    ? (value as unknown[]).map((v, i) => [String(i), v] as const)
    : Object.entries(value as Record<string, unknown>)

  const count = entries.length
  const summary = isArray ? `[ ${count} items ]` : `{ ${count} keys }`

  return (
    <div>
      <div
        className={`${classes.jsonRow} ${classes.jsonBranchRow}`}
        style={{ paddingLeft: depth * 14 }}
        onClick={() => setOpen(prev => !prev)}
      >
        <span className={classes.jsonToggle}>
          <span
            className='material-symbols-outlined'
            style={{ fontSize: 16 }}
          >
            {open ? 'expand_more' : 'chevron_right'}
          </span>
        </span>
        <span className={classes.jsonKey}>{name}</span>
        <span className={classes.jsonColon}>:</span>
        {!open && <span className={classes.jsonSummary}>{summary}</span>}
      </div>
      {open &&
        (count === 0 ? (
          <div
            className={classes.jsonRow}
            style={{ paddingLeft: (depth + 1) * 14 }}
          >
            <span className={classes.jsonSummary}>{summary}</span>
          </div>
        ) : (
          entries.map(([childName, childValue]) => (
            <JsonNode
              key={childName}
              name={childName}
              value={childValue}
              depth={depth + 1}
            />
          ))
        ))}
    </div>
  )
}

const JsonView: React.FC<JsonViewProps> = ({ data }) => {
  const classes = useStyles()

  if (!isPlainObject(data) && !Array.isArray(data)) {
    return (
      <div className={classes.jsonTree}>
        <div className={classes.jsonRow}>
          <PrimitiveValue value={data} />
        </div>
      </div>
    )
  }

  const entries = Array.isArray(data)
    ? data.map((v, i) => [String(i), v] as const)
    : Object.entries(data)

  return (
    <div className={classes.jsonTree}>
      {entries.map(([name, value]) => (
        <JsonNode
          key={name}
          name={name}
          value={value}
          depth={0}
        />
      ))}
    </div>
  )
}

export default JsonView
